import {
  getCouponTicketLineBase,
  getTicketCoupon,
  resolveCouponReach,
  shareTicketAmountOff,
  sharesTicketAmountOff,
  spreadTicketLineShare,
} from '@repo/lib';
import { createCartLineItem } from './createCartLineItem';
import getTicketTypePricing from './getTicketTypePricing';

export default function getTicketTypeLineItemsWithTotals(
  ticketTypes,
  ticketTypeDiscounts,
  coupon,
  taxRate,
  currency,
  pageId,
  pageTicketTypes = [],
  couponBumpLines = [],
  couponReach = undefined,
) {
  if (!ticketTypes || !ticketTypes.length) {
    return {
      totalTicketTypesAmount: 0,
      totalTicketTypesBulkDiscountAmount: 0,
      totalTicketTypesCouponDiscountAmount: 0,
      totalTicketFeesAmount: 0,
      totalTicketFeesSubtotalAmount: 0,
      totalPaidTicketQuantity: 0,
      lineItems: [],
    };
  }

  const reach =
    couponReach ??
    resolveCouponReach(coupon, {
      pageId,
      pageTicketTypeIds: pageTicketTypes,
      ticketTypeIds: ticketTypes.filter(t => Number(t.quantity) > 0).map(t => t._id),
      bumpProductIds: couponBumpLines.map(line => line.productId),
    });
  const isTicketTypeCoupon = reach.primaryScope === 'ticket';
  const sharesAmountOff = sharesTicketAmountOff(coupon, reach);
  const ticketCoupon = getTicketCoupon(coupon, reach);

  // The ticket lines before the coupon, which share a ticket-restricted amount off with the add-ons.
  const ticketCouponLines = ticketTypes.map(ticketType => {
    const quantity = Number(ticketType.quantity) || 0;
    const { totalAmount, fee } = getTicketTypePricing({
      ticketType,
      unitPrice: Number(ticketType.price) || 0,
      quantity,
      ticketTypeDiscounts,
      coupon: null,
      isTicketTypeCoupon: false,
    });

    return {
      ticketTypeId: ticketType._id,
      quantity,
      priceTotal: Math.max(0, Math.round(totalAmount)),
      base: getCouponTicketLineBase({
        unitPrice: quantity > 0 ? totalAmount / quantity : 0,
        quantity,
        feeAmount: fee?.amount ?? 0,
        appliesToTicketFees: coupon?.appliesToTicketFees,
      }),
    };
  });
  const couponShares =
    isTicketTypeCoupon && sharesAmountOff
      ? shareTicketAmountOff(coupon, reach, {
          ticketLines: ticketCouponLines,
          bumps: couponBumpLines,
        }).ticketDiscounts
      : ticketCouponLines.map(() => 0);

  const totals = ticketTypes.reduce(
    (acc, ticketType, index) => {
      // Ensure price and quantity are numbers
      const price = Number(ticketType.price) || 0;
      const quantity = Number(ticketType.quantity) || 0;

      const { subtotal, bulkDiscount, bulkDiscountAmount, couponDiscountAmount, totalAmount, fee } =
        getTicketTypePricing({
          ticketType,
          unitPrice: price,
          quantity,
          ticketTypeDiscounts,
          coupon: ticketCoupon,
          isTicketTypeCoupon,
          sharesAmountOff,
          couponShare: couponShares[index],
        });
      // A shared line's tickets are charged in whole cents, so some can reach $0 before the rest.
      const paidQuantity =
        couponShares[index] > 0
          ? spreadTicketLineShare({
              priceTotal: ticketCouponLines[index].priceTotal,
              quantity,
              share: couponShares[index],
            })
              .filter(group => group.price > 0)
              .reduce((sum, group) => sum + group.quantity, 0)
          : totalAmount > 0
            ? quantity
            : 0;

      const lineItem = {
        ...createCartLineItem({
          type: 'ticket',
          id: ticketType._id,
          name: ticketType.name,
          description: ticketType.reference || ticketType.description,
          quantity,
          unitPrice: price,
          appliedBulkDiscountRule: bulkDiscount,
          appliedCoupon: coupon,
          bulkDiscountAmount,
          couponDiscountAmount,
          currency,
          taxRate,
        }),
        ...(fee && { fee: { ...fee, display: ticketType.feeDisplay || 'included' } }),
      };

      return {
        totalTicketTypesAmount: acc.totalTicketTypesAmount + subtotal,
        totalTicketTypesBulkDiscountAmount:
          acc.totalTicketTypesBulkDiscountAmount + bulkDiscountAmount,
        // The fee's coupon share is netted into `fee.totalAmount` rather than
        // added here, so the ticket subtotal and the fee pool stay separate.
        totalTicketTypesCouponDiscountAmount:
          acc.totalTicketTypesCouponDiscountAmount + couponDiscountAmount,
        totalTicketFeesAmount: acc.totalTicketFeesAmount + (fee ? fee.totalAmount : 0),
        totalTicketFeesSubtotalAmount: acc.totalTicketFeesSubtotalAmount + (fee ? fee.subtotal : 0),
        // The API counts a ticket as paid on its charged price, after the ticket
        // tier and ticket coupon, so a ticket they bring to $0 does not count.
        totalPaidTicketQuantity: acc.totalPaidTicketQuantity + paidQuantity,
        lineItems: [...acc.lineItems, lineItem],
      };
    },
    {
      totalTicketTypesAmount: 0,
      totalTicketTypesBulkDiscountAmount: 0,
      totalTicketTypesCouponDiscountAmount: 0,
      totalTicketFeesAmount: 0,
      totalTicketFeesSubtotalAmount: 0,
      totalPaidTicketQuantity: 0,
      lineItems: [],
    },
  );

  return {
    ...totals,
    ticketCouponLines,
    ticketCouponShares: Object.fromEntries(
      ticketTypes.map((ticketType, index) => [ticketType._id, couponShares[index]]),
    ),
  };
}
